import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
  fetchFilters,
  selectEnabledKeys,
  selectDisabledKeys,
  updateSelection,
  syncPreviousSelection,
  selectLoading,
} from '../../features/filters/filterSlice';
import { fetchPerformanceTableDataThunk } from '../../features/performanceTable/performanceTableSlice';
import { fetchSegment, setIsPackReplacable } from '../../features/packSegments/packSegmentsSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { resetForm, setSelectedReplacedPack } from '../FormComponent/formSlice';
import { resetInputKpiState } from '../../features/inputKpi/inputKpiSlice';
import { resetBrandValues } from '../../features/brand/brandSlice';
import DropdownMenu from './DropdownMenu';
import { isEqual } from '../../utils/helper';
import { selectSelectedMetric } from '../../features/filters/metricsSlice';
import { fetchMapMetrics } from '../../features/map/mapSlice';

interface FilterDropdownProps {
  filterKey: string;
  label: string;
  singleSelect?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ filterKey, label, singleSelect = false }) => {
  const dispatch = useAppDispatch();

  const enabledKeys = useAppSelector((state) => selectEnabledKeys(state, filterKey));
  const disabledKeys = useAppSelector((state) => selectDisabledKeys(state, filterKey));
  const filtersState = useAppSelector((state) => state.filters);
  const loading = useAppSelector(selectLoading);
  const selectedMetric = useAppSelector(selectSelectedMetric);
  const allKeys = [...enabledKeys, ...disabledKeys];
  const [selectedValues, setSelectedValues] = useState<string[]>(singleSelect ? [enabledKeys[0]] : enabledKeys);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(singleSelect ? false : enabledKeys.length === allKeys.length);

  useEffect(() => {
    setSelectedValues(singleSelect ? [enabledKeys[0]] : enabledKeys);
    setSelectAll(singleSelect ? false : enabledKeys.length === allKeys.length);
  }, [enabledKeys, allKeys.length, singleSelect]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    let newSelectedValues;
    if (singleSelect) {
      newSelectedValues = [value];
    } else {
      const isChecked = event.target.checked;
      newSelectedValues = isChecked ? [...selectedValues, value] : selectedValues.filter((item) => item !== value);
    }

    setSelectedValues(newSelectedValues);
    setSelectAll(newSelectedValues.length === allKeys.length && !singleSelect);
    dispatch(updateSelection({ key: filterKey, value: newSelectedValues }));

    if (singleSelect) {
      setIsOpen(true);
    }
  };

  const handleSelectAllToggle = () => {
    if (singleSelect) return;

    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedValues(newSelectAll ? allKeys : []);

    dispatch(updateSelection({ key: filterKey, value: newSelectAll ? allKeys : [] }));
  };

  const handleClose = async () => {
    setIsOpen(false);

    if (!isEqual(filtersState.currentSelection, filtersState.previousSelection)) {
      await dispatch(showLoader());
      const { changedKeys } = generatePayload(filtersState);
      const filterPayload = {
        bottler: filtersState.currentSelection.bottler,
        state: filtersState.currentSelection.state,
        category: filtersState.currentSelection.category,
      };
      const filtersResponse: any = await dispatch(fetchFilters(filterPayload));

      await dispatch(syncPreviousSelection());
      await dispatch(resetForm());
      await dispatch(resetBrandValues());
      await dispatch(resetInputKpiState());
      await dispatch(setSelectedReplacedPack(null));
      await dispatch(setIsPackReplacable(false));

      if (label === 'Categories:') {
        const payload = {
          category: filtersState.currentSelection.category,
          metric: selectedMetric[0],
        };
        await dispatch(fetchMapMetrics(payload));
      }

      if (!changedKeys.includes('category') || changedKeys.length > 1) {
        const payloadForTable = {
          bottler: filtersResponse.payload.enabled_keys.bottler,
          state: filtersResponse.payload.enabled_keys.state,
        };
        await dispatch(fetchPerformanceTableDataThunk(payloadForTable));
      }
      const payloadForPackSegment = {
        bottler: filtersResponse.payload.enabled_keys.bottler,
        state: filtersResponse.payload.enabled_keys.state,
        category: filtersResponse.payload.enabled_keys.category,
      };
      await dispatch(fetchSegment(payloadForPackSegment));
      await dispatch(hideLoader());
    }
  };

  const generatePayload = (filtersState: any) => {
    const payload: Record<string, any[]> = {};
    const changedKeys: string[] = [];

    for (const key in filtersState.currentSelection) {
      if (!isEqual(filtersState.currentSelection[key], filtersState.previousSelection[key])) {
        payload[key] = filtersState.currentSelection[key] || [];
        changedKeys.push(key);
      }
    }

    if (changedKeys.includes('bottler') || changedKeys.includes('state')) {
      if (!payload['category']) {
        payload['category'] = filtersState.currentSelection['category'] || [];
      }
    }

    return { payload, changedKeys };
  };

  return (
    <DropdownMenu
      label={label}
      selectedValues={selectedValues}
      allKeys={allKeys}
      singleSelect={singleSelect}
      selectAll={selectAll}
      isOpen={isOpen}
      loading={loading}
      setIsOpen={setIsOpen}
      handleSelectAllToggle={handleSelectAllToggle}
      handleChange={handleChange}
      handleClose={handleClose}
      customStyles={{ width: '86px', maxWidth: '86px', border: `1px solid #E81C0E !important`, color: '#82868C' }}
    />
  );
};

export default FilterDropdown;
